/**
 * useCheckinJournal - 打卡流水数据处理 Hook
 *
 * 本 hook 负责处理两种不同类型任务的流水数据：
 * 1. 每日打卡任务（有 streakConfig）：用于里程碑组件，计算最大连续打卡天数
 * 2. 物料打卡任务（无 streakConfig）：用于展示今日已浏览、累计已阅读等统计数据
 *
 * 任务类型通过 configSnapshot.tasks 中的 streakConfig 字段区分：
 * - 有 streakConfig：每日打卡任务（每天最多一条有效流水，dedupPolicy.target="action"）
 * - 无 streakConfig：物料打卡任务（每天每个物料可以有一条流水，dedupPolicy.target="target_id"）
 */

import { ref, type Ref } from 'vue-demi';
import type { TaskSnapshot } from './usePageData';

/**
 * 流水条目接口
 */
export interface JournalEntry {
  id: string;
  taskId: string;
  actionCode?: string;
  actionTime: string;
  targetId?: string;
  actionContext?: Record<string, unknown>;
}

/**
 * 每日打卡统计结果
 */
export interface DailyCheckinStats {
  /** 最大连续打卡天数（历史最高） */
  maxStreak: number;
  /** 当前连续打卡天数 */
  currentStreak: number;
  /** 打卡日期列表（去重后的日期字符串，格式：YYYY-MM-DD） */
  checkinDates: string[];
}

/**
 * 任务维度的打卡进度（由 loadProgressData 组装，同步给 checkin-progress 组件）
 *
 * currentValue 由后端按任务模式（累计/连续）计算后直接下发。
 */
export interface DailyCheckinTaskProgress {
  /** 当前有效进度值（累计或连续，由后端按任务模式计算） */
  currentValue: number;
}

/**
 * 文章打卡统计结果
 */
export interface ArticleCheckinStats {
  /** 今日已浏览数量 */
  todayViewCount: number;
  /** 累计已阅读篇数 */
  totalCount: number;
  /** 累计已浏览时长（秒），每篇默认 20 秒 */
  totalDurationSeconds: number;
  /** 今日已浏览的物料 ID 集合 */
  todayViewedMaterialIds: Set<string>;
}

/** 每篇文章默认阅读时长（秒） */
const DEFAULT_ARTICLE_DURATION_SECONDS = 20;

/**
 * 格式化日期为 YYYY-MM-DD 格式
 */
export function formatDateToYMD(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * 计算连续打卡天数
 *
 * @param checkinDates - 打卡日期列表（YYYY-MM-DD 格式），需要已排序（从早到晚）
 * @param referenceDate - 参考日期（用于判断当前连续是否有效，默认为今天）
 * @returns { maxStreak, currentStreak }
 */
export function calculateStreaks(
  checkinDates: string[],
  referenceDate: Date = new Date(),
): { maxStreak: number; currentStreak: number } {
  if (checkinDates.length === 0) {
    return { maxStreak: 0, currentStreak: 0 };
  }

  // 去重并排序（从早到晚）
  const uniqueDates = Array.from(new Set(checkinDates)).sort();

  let maxStreak = 1;
  let currentStreak = 1;
  let tempStreak = 1;

  // 遍历计算连续天数
  for (let i = 1; i < uniqueDates.length; i++) {
    const prevDate = new Date(uniqueDates[i - 1]);
    const currDate = new Date(uniqueDates[i]);

    // 计算两个日期之间的天数差
    const diffDays = Math.round(
      (currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffDays === 1) {
      // 连续
      tempStreak++;
    } else {
      // 断连，重新开始计数
      tempStreak = 1;
    }

    maxStreak = Math.max(maxStreak, tempStreak);
  }

  // 计算当前连续天数
  // 需要检查最后一次打卡是否在今天或昨天，否则当前连续为 0
  const todayStr = formatDateToYMD(referenceDate);
  const yesterday = new Date(referenceDate);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = formatDateToYMD(yesterday);

  const lastCheckinDate = uniqueDates[uniqueDates.length - 1];

  if (lastCheckinDate === todayStr || lastCheckinDate === yesterdayStr) {
    // 从最后一天往前数连续天数
    currentStreak = 1;
    for (let i = uniqueDates.length - 2; i >= 0; i--) {
      const currDate = new Date(uniqueDates[i + 1]);
      const prevDate = new Date(uniqueDates[i]);
      const diffDays = Math.round(
        (currDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24),
      );

      if (diffDays === 1) {
        currentStreak++;
      } else {
        break;
      }
    }
  } else {
    // 已经断连
    currentStreak = 0;
  }

  return { maxStreak, currentStreak };
}

/**
 * 计算每日打卡任务统计数据
 *
 * @param entries - 每日打卡任务的流水条目
 * @param referenceDate - 参考日期（默认为今天）
 * @returns DailyCheckinStats
 */
export function calculateDailyCheckinStats(
  entries: JournalEntry[],
  referenceDate: Date = new Date(),
): DailyCheckinStats {
  if (entries.length === 0) {
    return {
      maxStreak: 0,
      currentStreak: 0,
      checkinDates: [],
    };
  }

  // 提取所有打卡日期（去重）
  const checkinDatesSet = new Set<string>();
  for (const entry of entries) {
    const date = new Date(entry.actionTime);
    checkinDatesSet.add(formatDateToYMD(date));
  }

  const checkinDates = Array.from(checkinDatesSet).sort();
  const { maxStreak, currentStreak } = calculateStreaks(
    checkinDates,
    referenceDate,
  );

  return {
    maxStreak,
    currentStreak,
    checkinDates,
  };
}

/**
 * 计算文章打卡任务统计数据
 *
 * @param entries - 文章打卡任务的流水条目
 * @param referenceDate - 参考日期（默认为今天，用于判断"今日"）
 * @returns ArticleCheckinStats
 */
export function calculateArticleCheckinStats(
  entries: JournalEntry[],
  referenceDate: Date = new Date(),
): ArticleCheckinStats {
  const todayStr = formatDateToYMD(referenceDate);

  const todayViewedMaterialIds = new Set<string>();
  let todayViewCount = 0;

  for (const entry of entries) {
    const entryDateStr = formatDateToYMD(new Date(entry.actionTime));

    if (entry.targetId && entryDateStr === todayStr) {
      todayViewedMaterialIds.add(entry.targetId);
      todayViewCount++;
    }
  }

  return {
    todayViewCount,
    totalCount: entries.length,
    totalDurationSeconds: entries.length * DEFAULT_ARTICLE_DURATION_SECONDS,
    todayViewedMaterialIds,
  };
}

/**
 * 判断任务是否为每日打卡任务（连续或累计模式均适用）
 *
 * 根据 dedupPolicy.target 判断：
 * - target === 'action'     → 每日打卡任务（每天任意一次动作即完成，包含连续和累计两种模式）
 * - target === 'target_id'  → 物料打卡任务（每天每个物料可打卡一次）
 *
 * 使用 dedupPolicy.target 而非 streakConfig，是因为累计每日打卡的 streakConfig 也为 null，
 * 与物料打卡无法区分；而 dedupPolicy.target 直接反映去重颗粒度，语义更准确。
 */
export function isDailyCheckinTask(task: TaskSnapshot): boolean {
  return task.dedupPolicy?.['target'] === 'action';
}

/**
 * useCheckinJournal Hook 配置
 */
export interface UseCheckinJournalOptions {
  /** 获取流水数据的函数 */
  fetchJournal: (taskId: string) => Promise<{ entries: JournalEntry[] }>;
  /** 任务配置列表（从 configSnapshot.tasks 获取） */
  tasks: TaskSnapshot[];
}

/**
 * useCheckinJournal Hook 返回值
 */
export interface UseCheckinJournalReturn {
  /** 是否正在加载 */
  loading: Ref<boolean>;
  /** 错误信息 */
  error: Ref<string>;
  /** 每日打卡统计数据 */
  dailyCheckinStats: Ref<DailyCheckinStats>;
  /** 文章打卡统计数据 */
  articleCheckinStats: Ref<ArticleCheckinStats>;
  /** 每日打卡流水条目 */
  dailyCheckinEntries: Ref<JournalEntry[]>;
  /** 文章打卡流水条目 */
  articleCheckinEntries: Ref<JournalEntry[]>;
  /** 判断物料是否今日已浏览 */
  isMaterialViewedToday: (materialId: string) => boolean;
  /** 加载流水数据 */
  loadJournalData: () => Promise<void>;
  /** 里程碑是否已达成（根据最大连续天数判断） */
  isMilestoneReached: (milestoneDays: number) => boolean;
}

/**
 * 打卡流水数据处理 Hook
 */
export function useCheckinJournal(
  options: UseCheckinJournalOptions,
): UseCheckinJournalReturn {
  const { fetchJournal, tasks } = options;

  const loading = ref(false);
  const error = ref('');

  const dailyCheckinEntries = ref<JournalEntry[]>([]);
  const articleCheckinEntries = ref<JournalEntry[]>([]);

  const dailyCheckinStats = ref<DailyCheckinStats>({
    maxStreak: 0,
    currentStreak: 0,
    checkinDates: [],
  });

  const articleCheckinStats = ref<ArticleCheckinStats>({
    todayViewCount: 0,
    totalCount: 0,
    totalDurationSeconds: 0,
    todayViewedMaterialIds: new Set(),
  });

  // 根据任务配置找出每日打卡任务和文章打卡任务的 ID
  const dailyCheckinTaskIds = new Set(
    tasks.filter(isDailyCheckinTask).map((t) => t.id),
  );
  const articleCheckinTaskIds = new Set(
    tasks.filter((t) => !isDailyCheckinTask(t)).map((t) => t.id),
  );

  /**
   * 加载流水数据
   */
  async function loadJournalData(): Promise<void> {
    loading.value = true;
    error.value = '';

    const allDailyEntries: JournalEntry[] = [];
    const allArticleEntries: JournalEntry[] = [];

    try {
      // 加载所有任务的流水数据
      const results = await Promise.allSettled(
        tasks.map((task) => fetchJournal(task.id)),
      );

      const referenceDate = new Date();

      // 遍历结果，根据 taskId 分类流水
      for (let i = 0; i < results.length; i++) {
        const result = results[i];
        const task = tasks[i];

        if (result.status === 'fulfilled') {
          const entries = result.value.entries;

          // 根据任务类型分类流水
          if (dailyCheckinTaskIds.has(task.id)) {
            // 每日打卡任务的流水
            allDailyEntries.push(...entries);
          } else if (articleCheckinTaskIds.has(task.id)) {
            // 文章打卡任务的流水
            allArticleEntries.push(...entries);
          }
        } else {
          console.error(
            '[useCheckinJournal] 加载任务流水失败:',
            task.id,
            result.reason,
          );
        }
      }

      // 更新每日打卡数据
      dailyCheckinEntries.value = allDailyEntries;
      dailyCheckinStats.value = calculateDailyCheckinStats(
        allDailyEntries,
        referenceDate,
      );

      // 更新文章打卡数据
      articleCheckinEntries.value = allArticleEntries;
      articleCheckinStats.value = calculateArticleCheckinStats(
        allArticleEntries,
        referenceDate,
      );
    } catch (err) {
      error.value = err instanceof Error ? err.message : '加载流水数据失败';
      console.error('[useCheckinJournal] 加载流水数据异常:', err);
    } finally {
      loading.value = false;
    }
  }

  /**
   * 判断物料是否今日已浏览
   */
  function isMaterialViewedToday(materialId: string): boolean {
    return articleCheckinStats.value.todayViewedMaterialIds.has(materialId);
  }

  /**
   * 判断里程碑是否已达成（根据最大连续天数）
   */
  function isMilestoneReached(milestoneDays: number): boolean {
    return dailyCheckinStats.value.maxStreak >= milestoneDays;
  }

  return {
    loading,
    error,
    dailyCheckinStats,
    articleCheckinStats,
    dailyCheckinEntries,
    articleCheckinEntries,
    isMaterialViewedToday,
    loadJournalData,
    isMilestoneReached,
  };
}

export default useCheckinJournal;
